import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

/**
 * Delta em relação ao dia anterior, no mesmo espírito de app_log_digest_delta() (PHP).
 * @param {number} current
 * @param {number} previous
 * @returns {{label: string, color: string}}
 */
function formatDelta(current, previous) {
  const delta = (current || 0) - (previous || 0);
  if (delta === 0) return { label: 'estável vs ontem', color: '#616161' };
  return {
    label: `${delta > 0 ? '+' : ''}${delta} vs ontem`,
    color: delta > 0 ? '#C62828' : '#2E7D32',
  };
}

/**
 * SummaryCard - Card individual de KPI (mesmo visual de FeedbackSummaryCards)
 */
function SummaryCard({ title, value, delta, icon, iconColor, iconBg }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, fontSize: '0.8125rem' }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', lineHeight: 1 }}>
            {value}
          </Typography>
          {delta && (
            <Typography variant="caption" sx={{ color: delta.color, fontWeight: 600, display: 'block', mt: 0.5 }}>
              {delta.label}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * LogSummaryCards - Cards de KPI do dia selecionado (Admin Geral > Logs)
 * @param {Object} props
 * @param {Object} props.summary - retorno de app_log_digest_build() (summary.php)
 */
function LogSummaryCards({ summary }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const s = summary || {};
  const totals = s.totals || {};
  const previousTotals = s.previous_totals || {};

  const total = s.total || 0;
  const previousTotal = Object.values(previousTotals).reduce((acc, v) => acc + (v || 0), 0);
  const errorsCount = s.errors_count ?? ((totals.error || 0) + (totals.critical || 0));
  const previousErrors = (previousTotals.error || 0) + (previousTotals.critical || 0);
  const warningsCount = s.warnings_count ?? (totals.warning || 0);
  const previousWarnings = previousTotals.warning || 0;

  const cards = [
    {
      title: 'Total de eventos',
      value: total,
      delta: formatDelta(total, previousTotal),
      icon: <EventNoteOutlinedIcon />,
      iconColor: '#5A80FB',
      iconBg: 'rgba(90, 128, 251, 0.1)',
    },
    {
      title: 'Erros',
      value: errorsCount,
      delta: formatDelta(errorsCount, previousErrors),
      icon: <ReportProblemOutlinedIcon />,
      iconColor: '#C62828',
      iconBg: '#FFEBEE',
    },
    {
      title: 'Avisos',
      value: warningsCount,
      delta: formatDelta(warningsCount, previousWarnings),
      icon: <WarningAmberOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(1, 1fr)' : 'repeat(3, 1fr)',
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, index) => (
        <SummaryCard key={index} {...card} />
      ))}
    </Box>
  );
}

export default LogSummaryCards;
